import { useRef } from 'react';
import { useNavigate } from 'react-router-dom';

import Button from '@components/button/Button';
import Tooltip from '@components/tooltip/Tooltip';
import { AccessMode, UserFlow } from '@constants/generic';
import { AUTH } from '@constants/routes';
import { STORAGE_KEYS } from '@constants/storage';
import { useAppDispatch } from '@core/store';
import { useLazyLogoutQuery } from '@features/authentication/queries/AuthQuery';
import { resetAuthState } from '@features/authentication/slices/AuthSlice';
import { resetGroupState } from '@features/group-management/slices/groupSlice';
import { resetMemberState } from '@features/member-management/slices/MemberSlice';
import { exitTrialMode } from '@features/trial-mode/slices/trialSlice';
import {
  hasAnyRoleScope,
  OWNER_OR_INDIVIDUAL_SCOPES
} from '@helpers/roleScope';
import useIsTruncated from '@hooks/useIsTruncated';
import { resetBranchState } from '@layouts/main-header/branchSlice';
import { resetUserState } from '@layouts/main-header/userSlice';
import {
  clearAllLocalStorageItems,
  getLocalStorageItem,
  setLocalStorageItem
} from '@utils/storage';
import { Org } from 'types/organization';

const ORG_NAME_TOOLTIP_ID = 'account-footer-org-name-tooltip';

const LINK_LABEL_STYLE =
  '!text-sm !font-medium !text-cofeePrimary6 whitespace-nowrap';

interface AccountFooterProps {
  orgList: Org[];
  onSwitchAccount?: () => void;
  onBeforeCreateAccount?: () => void;
}

const AccountFooter = ({
  orgList,
  onSwitchAccount,
  onBeforeCreateAccount
}: AccountFooterProps) => {
  const navigate = useNavigate();
  const dispatch = useAppDispatch();

  const currentOrgId = getLocalStorageItem(STORAGE_KEYS.ORGANIZATION_ID);
  const currentOrgName = orgList.find((org) => org.id === currentOrgId)?.name;
  // Retain the last known name so the footer doesn't flicker empty while
  // logging out clears localStorage just before the page reloads.
  const lastOrgNameRef = useRef<string>();
  if (currentOrgName) lastOrgNameRef.current = currentOrgName;
  const orgName = currentOrgName || lastOrgNameRef.current;

  const userId = getLocalStorageItem(STORAGE_KEYS.USER_ID);
  const [logout, { isFetching: isLoggingOut }] = useLazyLogoutQuery();

  const { ref: orgNameRef, isTruncated: isOrgNameTruncated } =
    useIsTruncated<HTMLSpanElement>(orgName);

  const hasActiveOrg = orgList.some(
    (org) => org.access_mode !== AccessMode.BLACKLISTED
  );

  // If the user owns any org (or is an individual account holder) hide "Create account".
  const hasOwnerOrg = orgList.some((org) =>
    hasAnyRoleScope(org, OWNER_OR_INDIVIDUAL_SCOPES)
  );

  const showSwitchAccount = hasActiveOrg && !!onSwitchAccount;
  const showCreateAccount = !hasActiveOrg && !hasOwnerOrg;

  const handleLogout = async () => {
    const res = await logout(userId);
    if ('data' in res) {
      dispatch(exitTrialMode());
      dispatch(resetGroupState());
      dispatch(resetUserState());
      dispatch(resetAuthState());
      dispatch(resetMemberState());
      dispatch(resetBranchState());
      clearAllLocalStorageItems();
      // Full reload so logout works even when the footer is shown on the
      // login route itself (where a client-side navigate would be a no-op).
      window.location.replace(AUTH.LOGIN);
    }
  };

  const handleCreateAccount = () => {
    onBeforeCreateAccount?.();
    setLocalStorageItem(STORAGE_KEYS.USER_FLOW, UserFlow.USER_ONBOARD);
    navigate(AUTH.SELECT_ACCOUNT);
  };

  return (
    <div className="flex items-center justify-center gap-x-0.5 border-t border-cofeeNeutral5 px-6 py-4 text-sm text-cofeeNeutral8">
      {orgName && (
        <>
          <span className="flex items-center gap-x-1 whitespace-nowrap">
            Signed in as
            <span
              ref={orgNameRef}
              className="max-w-[160px] truncate"
              data-tooltip-id={
                isOrgNameTruncated ? ORG_NAME_TOOLTIP_ID : undefined
              }
            >
              {orgName}
            </span>
          </span>
          {isOrgNameTruncated && (
            <Tooltip
              id={ORG_NAME_TOOLTIP_ID}
              type="text"
              tooltipText={orgName}
              place="top"
              className="!max-w-78 whitespace-normal break-words text-center"
            />
          )}
          <span className="mx-1 text-cofeeNeutral6">|</span>
        </>
      )}
      {showSwitchAccount && (
        <>
          <Button
            variant="transparent"
            size="none"
            label="Switch account"
            handleClick={onSwitchAccount}
            labelStyle={LINK_LABEL_STYLE}
          />
          <span className="mx-0.5 text-cofeeNeutral6">&bull;</span>
        </>
      )}
      {showCreateAccount && (
        <>
          <Button
            variant="transparent"
            size="none"
            label="Create account"
            handleClick={handleCreateAccount}
            labelStyle={LINK_LABEL_STYLE}
          />
          <span className="mx-0.5 text-cofeeNeutral6">&bull;</span>
        </>
      )}
      <div className="flex items-center gap-x-1.5">
        <Button
          variant="transparent"
          size="none"
          label="Log out"
          handleClick={handleLogout}
          disabled={isLoggingOut}
          labelStyle={LINK_LABEL_STYLE}
        />
      </div>
    </div>
  );
};

export default AccountFooter;
